import "bootstrap/dist/css/bootstrap.min.css";
import { Form, Row, Container, Card, Button } from "react-bootstrap";

import ContentLayout from "../components/ContentLayout";
import { ChangeEvent, CSSProperties, FocusEvent, FormEvent, useState } from "react";
import { useSelector } from "react-redux";

import { BackendApi } from "../utils/BackendApi";

import toast, { Toaster } from "react-hot-toast";

import { SystemMsg } from "../utils/SystemMsg";
import common from "../utils/common";
import { ApiRoute } from "../utils/ApiRoute";
import { RootState } from "../store/store";
import { useI18n } from "../i18n";

const Title = () => {
  const { t } = useI18n();
  const title = t("nav.settings") + " - " + t("meta.appName");
  return (
    <>
      <title>{title}</title>
      
    </>
  );
};

type PasswordFields = {
  password: string;
  newPassword: string;
  passwordConfirm: string;
};

function formString(data: FormData, key: string): string {
  const value = data.get(key);
  return typeof value === "string" ? value : "";
}

function readPasswordFields(form: HTMLFormElement): PasswordFields {
  const data = new FormData(form);
  return {
    password: formString(data, "password"),
    newPassword: formString(data, "newPassword"),
    passwordConfirm: formString(data, "passwordConfirm"),
  };
}

function passwordValidationResult(fields: PasswordFields) {
  const p1empty = common.stringIsNullOrEmpty(fields.password);
  const p2empty = common.stringIsNullOrEmpty(fields.newPassword);
  const p3empty = common.stringIsNullOrEmpty(fields.passwordConfirm);
  const newPwNotMatch =
    !p2empty && !p3empty && fields.newPassword !== fields.passwordConfirm;
  return {
    p1empty,
    p2empty,
    p3empty,
    newPwNotMatch,
  };
}

const Content = () => {
  const { t } = useI18n();
  const session = useSelector((state: RootState) => state.current.session);

  const [pwInvalid, setPwInvalid] = useState(false);
  const [pwInvalidMsg, setPwInvalidMsg] = useState(
    SystemMsg.Settings.password_empty
  );

  const [newPwInvalid, setNewPwInvalid] = useState(false);
  const [newPwInvalidMsg, setNewPwInvalidMsg] = useState(
    SystemMsg.Settings.password_empty
  );

  const [confPwInvalid, setConfPwInvalid] = useState(false);
  const [confPwInvalidMsg, setConfPwInvalidMsg] = useState(
    SystemMsg.Settings.password_empty
  );

  const UserIconDummy = ({ email }: { email: string }) => {
    const getUserIconText = (username: string) => {
      let abbrv = "";
      if (username.toString().length > 0) {
        abbrv = username[0].toString().toUpperCase();
        if (username.toString().length > 1) {
          abbrv =
            username[0].toString().toUpperCase() +
            username[username.length - 1].toString().toUpperCase();
        }
      }
      return abbrv;
    };
    const username = email?.split(`@`)[0] ?? "";
    const abbrv = getUserIconText(username);
    const style = {
      borderRadius: "50%",
      backgroundColor: "#e5e5e5",
      display: "inline-flex",
      height: "4vw",
      width: "4vw",
      maxWidth: "85px",
      maxHeight: "85px",
      fontSize: "2rem",
      marginRight: "20px",
    } as CSSProperties;

    const styleInner = {
      display: "flex",
      justifyContent: "center" /* Centers horizontally */,
      alignItems: "center" /* Centers vertically */,
      height: "100%",
      width: "100%",
      userSelect: "none",
    } as CSSProperties;

    return (
      <>
        <div style={style}>
          <div style={styleInner}>{abbrv}</div>
        </div>
      </>
    );
  };

  const passwordOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const form = event.currentTarget.form;
    if (!form) {
      return;
    }
    setPwInvalidMsg(SystemMsg.Settings.password_empty);
    const validation = passwordValidationResult(readPasswordFields(form));
    if (validation.p1empty && validation.p2empty && validation.p3empty) {
      setPwInvalid(false);
      return;
    }
    if (validation.p1empty && (!validation.p2empty || !validation.p3empty)) {
      setPwInvalid(true);
      return;
    }
    setPwInvalid(false);
  };

  const newPasswordOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const form = event.currentTarget.form;
    if (!form) {
      return;
    }
    setNewPwInvalidMsg(SystemMsg.Settings.password_empty);
    const validation = passwordValidationResult(readPasswordFields(form));
    if (validation.p2empty && !validation.p3empty) {
      setNewPwInvalid(true);
      return;
    }
    if (validation.newPwNotMatch) {
      setNewPwInvalid(true);
      setNewPwInvalidMsg(SystemMsg.Settings.confirmPasswordNotMatch);
      return;
    }
    setNewPwInvalid(false);
    setConfPwInvalid(false);
  };

  const passwordConfirmOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const form = event.currentTarget.form;
    if (!form) {
      return;
    }
    setConfPwInvalidMsg(SystemMsg.Settings.password_empty);
    const validation = passwordValidationResult(readPasswordFields(form));
    if (validation.p3empty && !validation.p2empty) {
      setConfPwInvalid(true);
      return;
    }
    if (validation.newPwNotMatch) {
      setConfPwInvalid(true);
      setConfPwInvalidMsg(SystemMsg.Settings.confirmPasswordNotMatch);
      return;
    }
    setNewPwInvalid(false);
    setConfPwInvalid(false);
  };

  const passwordOutFocus = (event: FocusEvent<HTMLInputElement>) => {
    const password = event.currentTarget.value;
    if (common.stringIsNullOrEmpty(password) || pwInvalid) {
      return;
    }
    const dataObj = { username: session.username, password: password };
    BackendApi.post(
      `${ApiRoute.login}`,
      dataObj,
      (resp) => {
        const success = resp.status === 200;
        if (!success) {
          setPwInvalid(true);
          setPwInvalidMsg(SystemMsg.Settings.password_incorrect);
        }
      },
      (resp) => {
        setPwInvalid(true);
        setPwInvalidMsg(SystemMsg.Settings.password_incorrect);
      }
    );
  };

  const saveUserSettings = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (session.id < 0 || !session.username) {
      toast.error(SystemMsg.Settings.userDetailNotFound);
      return;
    }
    const fields = readPasswordFields(event.currentTarget);
    const validation = passwordValidationResult(fields);
    let currentInvalid = pwInvalid;
    let newInvalid = false;
    let confirmInvalid = false;
    let newMsg = SystemMsg.Settings.password_empty;
    let confirmMsg = SystemMsg.Settings.password_empty;

    if (validation.p1empty && (!validation.p2empty || !validation.p3empty)) {
      currentInvalid = true;
      setPwInvalidMsg(SystemMsg.Settings.password_empty);
    }
    if (validation.p2empty && !validation.p3empty) {
      newInvalid = true;
    } else if (validation.newPwNotMatch) {
      newInvalid = true;
      newMsg = SystemMsg.Settings.confirmPasswordNotMatch;
    }
    if (validation.p3empty && !validation.p2empty) {
      confirmInvalid = true;
    } else if (validation.newPwNotMatch) {
      confirmInvalid = true;
      confirmMsg = SystemMsg.Settings.confirmPasswordNotMatch;
    }

    setPwInvalid(currentInvalid);
    setNewPwInvalid(newInvalid);
    setNewPwInvalidMsg(newMsg);
    setConfPwInvalid(confirmInvalid);
    setConfPwInvalidMsg(confirmMsg);
    if (currentInvalid || newInvalid || confirmInvalid) {
      return;
    }

    BackendApi.put(
      `${ApiRoute.user}`,
      {
        password: fields.passwordConfirm,
      },
      (resp) => {
        const success = resp.status === 200;
        if (success) {
          toast.success(SystemMsg.Settings.updateSuccess);
        } else {
          toast.error(resp.data.message);
        }
      },
      (resp) => {
        console.log(resp);
      }
    );
  };

  const resetSettings = () => {
    setPwInvalid(false);
    setPwInvalidMsg(SystemMsg.Settings.password_empty);

    setNewPwInvalid(false);
    setNewPwInvalidMsg(SystemMsg.Settings.password_empty);

    setConfPwInvalid(false);
    setConfPwInvalidMsg(SystemMsg.Settings.password_empty);
  };
  return (
    <>
      <Container style={{ maxWidth: "45vw" }}>
        <h4 className="mb-4">{t("settings.title")}</h4>
        <Card style={{ width: "100%", backgroundColor: "rgb(248, 249, 250)" }}>
          <Card.Body>
            <UserIconDummy email={session.username} />
            <Card.Title style={{ display: "inline-flex" }}>
              {session.username ?? ""}
            </Card.Title>
            <Card.Text></Card.Text>
          </Card.Body>
        </Card>
        <Row className="mt-3"></Row>
        <Card style={{ width: "100%", backgroundColor: "" }}>
          <Card.Body>
            <Form onSubmit={saveUserSettings}>
              <Form.Group className="mb-3">
                <Form.Label>{t("fields.currentPassword")}</Form.Label>
                <Form.Control
                  name="password"
                  placeholder={t("fields.currentPassword")}
                  type="password"
                  onChange={passwordOnChange}
                  isInvalid={pwInvalid}
                  onBlur={passwordOutFocus}
                />
                <Form.Control.Feedback type="invalid">
                  {pwInvalidMsg}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t("fields.newPassword")}</Form.Label>
                <Form.Control
                  name="newPassword"
                  placeholder={t("fields.newPassword")}
                  type="password"
                  onChange={newPasswordOnChange}
                  isInvalid={newPwInvalid}
                />
                <Form.Control.Feedback type="invalid">
                  {newPwInvalidMsg}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t("fields.confirmPassword")}</Form.Label>
                <Form.Control
                  name="passwordConfirm"
                  placeholder={t("fields.confirmPassword")}
                  type="password"
                  onChange={passwordConfirmOnChange}
                  isInvalid={confPwInvalid}
                />
                <Form.Control.Feedback type="invalid">
                  {confPwInvalidMsg}
                </Form.Control.Feedback>
              </Form.Group>
              <div style={{ float: "right" }}>
                <Button
                  variant="secondary"
                  type="submit"
                  disabled={pwInvalid || newPwInvalid || confPwInvalid}
                >
                  {t("common.save")}
                </Button>{" "}
                <Button
                  variant="secondary-outline"
                  type="reset"
                  onClick={resetSettings}
                >
                  {t("common.reset")}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          className: "",
          style: common.toasterStyle,
        }}
      />
    </>
  );
};
const Settings = () => {
  return (
      <ContentLayout header={<Title />} content={<Content />} />
  );
};
export default Settings;
